import React, { useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { useChatSubmit } from '../../hooks/useChatSubmit';

export default function PolyChatInput({ sessionId, createSession = false }) {
  const [input, setInput] = useState('');
  const [showHint, setShowHint] = useState(false);
  const textareaRef = useRef(null);
  const { submitMessage, isSubmitting } = useChatSubmit({ sessionId, createSession });
  const hasSession = Boolean(sessionId) || createSession;
  const isDisabled = !hasSession || isSubmitting;

  const handleSend = async () => {
    if (!hasSession || !input.trim() || isSubmitting) return;
    const text = input.trim();
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    await submitMessage(text);
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  return (
    <div
      className="relative max-w-3xl mx-auto flex items-end gap-2 w-full"
      onMouseEnter={() => {
        if (!hasSession) setShowHint(true);
      }}
      onMouseLeave={() => setShowHint(false)}>
      {showHint && !hasSession && (
        <div className="pointer-events-none absolute -top-10 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-shell-border bg-shell-card px-3 py-1.5 text-xs text-ink shadow-soft">
          Click <span className="text-space font-medium">New Chat</span> to start a session
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={hasSession ? input : ''}
        onChange={e => {
          if (!hasSession) return;
          setInput(e.target.value);
        }}
        onInput={handleInput}
        onKeyDown={e => {
          if (!hasSession) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        disabled={!hasSession}
        readOnly={!hasSession}
        placeholder={hasSession ? 'Send a message...' : 'Click New Chat to start a session...'}
        rows={1}
        title={!hasSession ? 'Click New Chat to start a session' : undefined}
        className={`flex-1 bg-shell-card border border-shell-border rounded-[22px] px-4 py-3 text-sm text-ink placeholder:text-mute outline-none resize-none max-h-32 shadow-soft ${
          hasSession
            ? 'focus:border-ink/40'
            : 'opacity-50 cursor-not-allowed focus:border-shell-border'
        }`}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={isDisabled || !input.trim()}
        title={!hasSession ? 'Click New Chat to start a session' : undefined}
        className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-space hover:bg-space/90 text-black rounded-full disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:-translate-y-px">
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
}
